import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Package, 
  AlertTriangle,
  RefreshCw,
  Settings,
  MapPin,
  Star,
  Zap,
  Eye,
  Filter,
  Download,
  Upload,
  DollarSign
} from 'lucide-react';
import AdminInventoryTable from './AdminInventoryTable';
import PriceControlModule from './PriceControlModule';
import './AdminInventoryManagement.css';

const AdminInventoryManagement = () => {
  const [analytics, setAnalytics] = useState({});
  const [stockMovements, setStockMovements] = useState([]);
  const [vendorPerformance, setVendorPerformance] = useState([]);
  const [allVendors, setAllVendors] = useState([]);
  const [totals, setTotals] = useState({ total_vendors: 0, total_products: 0, total_stock: 0, active_reservations: 0 });
  const [adminSummary, setAdminSummary] = useState({ totalProducts: 0, totalStockOnHand: 0, totalReservedStock: 0, availableStock: 0 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [showInventoryTable, setShowInventoryTable] = useState(false);
  const [showPriceControl, setShowPriceControl] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const getAdminKey = () => localStorage.getItem('ADMIN_API_KEY') || localStorage.getItem('adminApiKey');
  const isAdmin = () => (localStorage.getItem('userType') === 'admin');
  const requireAdminAuth = async () => {
    // Prefer admin JWT set during admin login
    const adminToken = localStorage.getItem('adminToken');
    const vendorToken = localStorage.getItem('token');
    const token = adminToken || vendorToken || '';
    // No prompts; legacy admin keys are optional for backward compatibility only
    const adminKey = getAdminKey();
    return { token, adminKey };
  };

  // Fetch inventory analytics
  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const { token, adminKey } = await requireAdminAuth();
      const response = await fetch('/api/inventory/analytics/performance', {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(adminKey ? { 'x-admin-key': adminKey } : {})
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch stock movements analytics
  const fetchStockMovements = async () => {
    try {
      const { token, adminKey } = await requireAdminAuth();
      const params = new URLSearchParams({
        days: selectedPeriod,
        ...(selectedVendor && { vendorId: selectedVendor })
      });
      
      const response = await fetch(`/api/inventory/analytics/stock-movements?${params}`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(adminKey ? { 'x-admin-key': adminKey } : {})
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStockMovements(data.movements || []);
      }
    } catch (error) {
      console.error('Error fetching stock movements:', error);
    }
  };

  // Fetch legacy aggregate totals for admin (existing dashboard numbers)
  const fetchTotals = async () => {
    try {
      const { token, adminKey } = await requireAdminAuth();
      // Use new summary endpoint
      const response = await fetch('/api/inventory/summary', {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(adminKey ? { 'x-admin-key': adminKey } : {})
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTotals({
          total_vendors: Number(data.total_vendors) || 0,
          total_products: Number(data.total_products) || 0,
          total_stock: Number(data.total_stock) || 0,
          active_reservations: Number(data.active_reservations) || 0
        });
        setToast({ type: 'success', message: 'Dashboard refreshed' });
      }
    } catch (error) {
      console.error('Error fetching totals:', error);
      setToast({ type: 'error', message: 'Failed to refresh totals' });
    }
  };

  // Fetch new Admin Inventory Summary (across all vendors)
  const fetchAdminSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const adminKey = localStorage.getItem('ADMIN_API_KEY') || localStorage.getItem('adminApiKey');
      const res = await fetch('/api/admin/inventory/summary', {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(adminKey ? { 'x-admin-key': adminKey } : {})
        }
      });
      if (res.ok) {
        const data = await res.json();
        setAdminSummary({
          totalProducts: Number(data.totalProducts) || 0,
          totalStockOnHand: Number(data.totalStockOnHand) || 0,
          totalReservedStock: Number(data.totalReservedStock) || 0,
          availableStock: Number(data.availableStock) || 0
        });
      }
    } catch (e) {
      console.error('fetchAdminSummary error:', e);
    }
  };

  // Fetch all vendors for dropdown
  const fetchAllVendors = async () => {
    try {
      const { token, adminKey } = await requireAdminAuth();
      const response = await fetch('/api/admin/vendors?limit=1000', {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(adminKey ? { 'x-admin-key': adminKey } : {})
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAllVendors(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching all vendors:', error);
    }
  };

  // Fetch vendor performance data
  const fetchVendorPerformance = async () => {
    try {
      const { token, adminKey } = await requireAdminAuth();
      const response = await fetch('/api/inventory/analytics/performance', {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(adminKey ? { 'x-admin-key': adminKey } : {})
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVendorPerformance(data.analytics || []);
      }
    } catch (error) {
      console.error('Error fetching vendor performance:', error);
    }
  };

  // Clean up expired reservations
  const cleanupExpiredReservations = async () => {
    try {
      setActionLoading(true);
      const { token, adminKey } = await requireAdminAuth();
      const response = await fetch('/api/inventory/cleanup', {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          ...(adminKey ? { 'x-admin-key': adminKey } : {})
        }
      });
      
      if (response.ok) {
        // If backend returned updated totals, consume them and refresh rest
        try {
          const data = await response.json();
          if (data?.totals) {
            setTotals({
              total_vendors: Number(data.totals.total_vendors) || 0,
              total_products: Number(data.totals.total_products) || 0,
              total_stock: Number(data.totals.total_stock) || 0,
              active_reservations: Number(data.totals.active_reservations) || 0
            });
          }
        } catch (_) {}
        refreshAll();
        setToast({ type: 'success', message: 'Cleanup completed' });
      } else {
        let errorText = '';
        try {
          const errJson = await response.json();
          errorText = errJson?.message || errJson?.error || '';
        } catch (_) {
          try {
            errorText = await response.text();
          } catch (_) {
            errorText = '';
          }
        }
        setToast({ type: 'error', message: `Failed to cleanup: ${errorText || `${response.status} ${response.statusText}`}` });
      }
    } catch (error) {
      console.error('Error cleaning up reservations:', error);
      setToast({ type: 'error', message: 'Error cleaning up reservations' });
    } finally {
      setActionLoading(false);
    }
  };

  // Unified refresh to reload all dashboard data and table rows
  const refreshAll = async () => {
    setActionLoading(true);
    try {
      await Promise.all([
        fetchAnalytics(),
        fetchStockMovements(),
        fetchTotals(),
        fetchAdminSummary(),
        fetchVendorPerformance(),
        fetchAllVendors()
      ]);
    } finally {
      setActionLoading(false);
    }
    setRefreshTick((t) => t + 1); // signal table to reload
  };

  useEffect(() => {
    fetchAnalytics();
    fetchStockMovements();
    fetchTotals();
    fetchAdminSummary();
    fetchVendorPerformance();
    fetchAllVendors();
  }, [selectedPeriod, selectedVendor]);

  const stats = [
    {
      title: 'Total Vendors',
      value: String(totals.total_vendors || 0),
      icon: Users,
      color: 'blue',
      description: 'Active vendors in system'
    },
    {
      title: 'Total Products',
      value: String(totals.total_products || 0),
      icon: Package,
      color: 'green',
      description: 'Products across all vendors'
    },
    {
      title: 'Total Stock',
      value: String(totals.total_stock || 0),
      icon: TrendingUp,
      color: 'purple',
      description: 'Total available stock'
    },
    {
      title: 'Active Reservations',
      value: String(totals.active_reservations || 0),
      icon: AlertTriangle,
      color: 'orange',
      description: 'Current reservations'
    }
  ];

  const movementTypes = {
    'in': 'Stock In',
    'out': 'Stock Out',
    'adjustment': 'Adjustment',
    'reservation': 'Reservation',
    'commit': 'Commit',
    'release': 'Release'
  };

  const getMovementIcon = (type) => {
    switch (type) {
      case 'in': return '↗️';
      case 'out': return '↘️';
      case 'reservation': return '🔒';
      case 'commit': return '✅';
      case 'release': return '🔓';
      default: return '📝';
    }
  };

  const getMovementColor = (type) => {
    switch (type) {
      case 'in': return 'success';
      case 'out': return 'danger';
      case 'reservation': return 'warning';
      case 'commit': return 'success';
      case 'release': return 'info';
      default: return 'secondary';
    }
  };

  return (
    <div className="admin-inventory-management">
      <div className="dashboard-header">
        <h1>Inventory Management</h1>
        <p>Monitor inventory performance, stock movements, and vendor analytics across the platform.</p>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={refreshAll} disabled={actionLoading}>
            <RefreshCw size={16} />
            {actionLoading ? 'Refreshing...' : 'Refresh Data'}
          </button>
          {isAdmin() && (
            <button className="btn btn-secondary" onClick={cleanupExpiredReservations} disabled={actionLoading}>
              <Settings size={16} />
              {actionLoading ? 'Cleaning...' : 'Cleanup Expired'}
            </button>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className={`stat-card stat-card-${stat.color}`}>
              <div className="stat-icon">
                <Icon size={24} />
              </div>
              <div className="stat-content">
                <h3 className="stat-title">{stat.title}</h3>
                <p className="stat-value">{stat.value}</p>
                <p className="stat-description">{stat.description}</p>
              </div>
            </div>
          );
        })}
        {/* Admin Inventory Summary cards */}
        <div className="stat-card stat-card-green">
          <div className="stat-icon"><Package size={24} /></div>
          <div className="stat-content">
            <h3 className="stat-title">Total Products</h3>
            <p className="stat-value">{adminSummary.totalProducts}</p>
            <p className="stat-description">Unique products across all vendors</p>
          </div>
        </div>
        <div className="stat-card stat-card-blue">
          <div className="stat-icon"><TrendingUp size={24} /></div>
          <div className="stat-content">
            <h3 className="stat-title">Total Stock On Hand</h3>
            <p className="stat-value">{adminSummary.totalStockOnHand}</p>
            <p className="stat-description">Sum of stock_on_hand</p>
          </div>
        </div>
        <div className="stat-card stat-card-orange">
          <div className="stat-icon"><AlertTriangle size={24} /></div>
          <div className="stat-content">
            <h3 className="stat-title">Total Reserved</h3>
            <p className="stat-value">{adminSummary.totalReservedStock}</p>
            <p className="stat-description">Sum of stock_reserved</p>
          </div>
        </div>
        <div className="stat-card stat-card-purple">
          <div className="stat-icon"><BarChart3 size={24} /></div>
          <div className="stat-content">
            <h3 className="stat-title">Available Stock</h3>
            <p className="stat-value">{adminSummary.availableStock}</p>
            <p className="stat-description">stock_on_hand - stock_reserved</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Period:</label>
          <select 
            value={selectedPeriod} 
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="form-select"
          >
            <option value="1">Last 24 hours</option>
            <option value="3">Last 3 days</option>
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
            <option value="60">Last 60 days</option>
            <option value="90">Last 90 days</option>
            <option value="180">Last 6 months</option>
            <option value="365">Last year</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Vendor:</label>
          <select 
            value={selectedVendor} 
            onChange={(e) => setSelectedVendor(e.target.value)}
            className="form-select"
          >
            <option value="">All Vendors ({allVendors.length})</option>
            {allVendors.map(vendor => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.shop_name || vendor.business_name} ({vendor.status})
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <button 
            className={`btn ${showAnalytics && !showInventoryTable && !showPriceControl ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setShowAnalytics(true); setShowInventoryTable(false); setShowPriceControl(false); }}
          >
            <BarChart3 size={16} />
            Analytics
          </button>
          <button 
            className={`btn ${!showAnalytics && !showInventoryTable && !showPriceControl ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setShowAnalytics(false); setShowInventoryTable(false); setShowPriceControl(false); }}
          >
            <Package size={16} />
            Stock Movements
          </button>
          <button 
            className={`btn ${showInventoryTable && !showPriceControl ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setShowAnalytics(false); setShowInventoryTable(true); setShowPriceControl(false); }}
          >
            <Package size={16} />
            Vendor Inventory
          </button>
          <button 
            className={`btn ${showPriceControl ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setShowAnalytics(false); setShowInventoryTable(false); setShowPriceControl(true); }}
          >
            <DollarSign size={16} />
            Price Control
          </button>
        </div>
      </div>

      {/* Analytics View */}
      {showAnalytics && !showInventoryTable && !showPriceControl && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Vendor Performance Analytics</h2>
            <div className="section-actions">
              <button className="btn btn-secondary btn-sm" onClick={fetchVendorPerformance}>
                <RefreshCw size={16} />
                Refresh
              </button>
            </div>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Products</th>
                  <th>Total Stock</th>
                  <th>Avg Stock/Product</th>
                  <th>Active Reservations</th>
                  <th>Reserved Quantity</th>
                  <th>Performance Score</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="loading-cell">Loading analytics...</td>
                  </tr>
                ) : vendorPerformance.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="empty-cell">No vendor data available</td>
                  </tr>
                ) : (
                  vendorPerformance.map((vendor) => (
                    <tr key={vendor.vendor_id}>
                      <td>
                        <div className="vendor-info">
                          <strong>{vendor.shop_name}</strong>
                          <span className="vendor-id">#{vendor.vendor_id}</span>
                        </div>
                      </td>
                      <td>{vendor.total_products || 0}</td>
                      <td className="stock-value">{vendor.total_stock || 0}</td>
                      <td>{Number(vendor.avg_stock_per_product || 0).toFixed(1)}</td>
                      <td className="reservation-count">{vendor.active_reservations || 0}</td>
                      <td className="reserved-quantity">{vendor.total_reserved_quantity || 0}</td>
                      <td>
                        <div className="performance-score">
                          <div className="score-bar">
                            <div 
                              className="score-fill" 
                              style={{ 
                                width: `${Math.min(100, (Number(vendor.total_stock) || 0) / 10)}%` 
                              }}
                            ></div>
                          </div>
                          <span className="score-text">
                            {Math.min(100, (Number(vendor.total_stock) || 0) / 10).toFixed(0)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stock Movements View */}
      {!showAnalytics && !showInventoryTable && !showPriceControl && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Stock Movement Analytics</h2>
            <div className="section-actions">
              <button className="btn btn-secondary btn-sm" onClick={fetchStockMovements}>
                <RefreshCw size={16} />
                Refresh
              </button>
              <button className="btn btn-primary btn-sm">
                <Download size={16} />
                Export
              </button>
            </div>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Movement Type</th>
                  <th>Count</th>
                  <th>Total Quantity</th>
                  <th>Date</th>
                  <th>Trend</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="loading-cell">Loading movements...</td>
                  </tr>
                ) : stockMovements.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="empty-cell">No movement data available</td>
                  </tr>
                ) : (
                  stockMovements.map((movement, index) => (
                    <tr key={index}>
                      <td>
                        <div className="movement-type">
                          <span className="movement-icon">
                            {getMovementIcon(movement.movement_type)}
                          </span>
                          <span className={`movement-badge movement-${getMovementColor(movement.movement_type)}`}>
                            {movementTypes[movement.movement_type] || movement.movement_type}
                          </span>
                        </div>
                      </td>
                      <td className="movement-count">{movement.movement_count}</td>
                      <td className="movement-quantity">{movement.total_quantity}</td>
                      <td>{new Date(movement.movement_date).toLocaleDateString()}</td>
                      <td>
                        <div className="trend-indicator">
                          {movement.movement_type === 'in' ? (
                            <TrendingUp size={16} className="trend-up" />
                          ) : movement.movement_type === 'out' ? (
                            <TrendingUp size={16} className="trend-down" />
                          ) : (
                            <Package size={16} className="trend-neutral" />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Vendor Inventory Table */}
      {showInventoryTable && !showPriceControl && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Vendor Inventory</h2>
          </div>
          <AdminInventoryTable refreshTick={refreshTick} />
        </div>
      )}

      {/* Price Control Module */}
      {showPriceControl && (
        <div className="dashboard-section">
          <PriceControlModule refreshTick={refreshTick} />
        </div>
      )}

      {/* Summary Cards */}
      <div className="summary-section">
        {toast && (
          <div className={`toast ${toast.type}`} onAnimationEnd={() => setToast(null)}>
            {toast.message}
          </div>
        )}
        <div className="summary-cards">
          <div className="summary-card">
            <div className="card-header">
              <h3>Top Performing Vendors</h3>
            </div>
            <div className="card-content">
              {vendorPerformance
                .sort((a, b) => (b.total_stock || 0) - (a.total_stock || 0))
                .slice(0, 5)
                .map((vendor, index) => (
                  <div key={vendor.vendor_id} className="vendor-item">
                    <div className="vendor-rank">#{index + 1}</div>
                    <div className="vendor-details">
                      <div className="vendor-name">{vendor.shop_name}</div>
                      <div className="vendor-stock">{vendor.total_stock || 0} units</div>
                    </div>
                    <div className="vendor-score">
                      <Star size={14} fill="gold" />
                      {Math.min(5, (Number(vendor.total_stock) || 0) / 20).toFixed(1)}
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className="summary-card">
            <div className="card-header">
              <h3>Movement Summary</h3>
            </div>
            <div className="card-content">
              {Object.entries(
                stockMovements.reduce((acc, movement) => {
                  acc[movement.movement_type] = (acc[movement.movement_type] || 0) + movement.movement_count;
                  return acc;
                }, {})
              ).map(([type, count]) => (
                <div key={type} className="movement-summary">
                  <div className="movement-type-info">
                    <span className="movement-icon">{getMovementIcon(type)}</span>
                    <span className="movement-label">{movementTypes[type] || type}</span>
                  </div>
                  <div className="movement-count">{count}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="summary-card">
            <div className="card-header">
              <h3>System Health</h3>
            </div>
            <div className="card-content">
              <div className="health-metric">
                <div className="metric-label">Active Vendors</div>
                <div className="metric-value">{vendorPerformance.length}</div>
              </div>
              <div className="health-metric">
                <div className="metric-label">Total Products</div>
                <div className="metric-value">
                  {vendorPerformance.reduce((sum, v) => sum + (Number(v.total_products) || 0), 0)}
                </div>
              </div>
              <div className="health-metric">
                <div className="metric-label">System Uptime</div>
                <div className="metric-value">99.9%</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminInventoryManagement;
